import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { dummyP2PAds, dummyP2PAdDetails } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./P2PAdDetailsPage.css";

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const P2PAdDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const adId = location.state?.adId;

  const ad = dummyP2PAds.find((a) => a._id === adId);
  const details = adId ? dummyP2PAdDetails[adId] : null;

  if (!ad) {
    return (
      <main className="dashboard-main dashboard-subpage p2p-ad-details-page">
        <div className="dashboard-body">
          <p>Ad not found.</p>
          <button type="button" className="p2p-ad-details-btn-back" onClick={() => navigate("/dashboard/p2p-ads")}>
            <i className="fas fa-arrow-left" /> Back to Ads List
          </button>
        </div>
      </main>
    );
  }

  const ownerName = ad.userInfo ? `${ad.userInfo.firstName || ""} ${ad.userInfo.lastName || ""}`.trim() || ad.userInfo.emailId : "N/A";
  const ownerEmail = ad.userInfo?.emailId ?? "N/A";
  const ownerPhone = details?.ownerPhone ?? "N/A";
  const minLimit = details?.minLimit ?? "N/A";
  const maxLimit = details?.maxLimit ?? "N/A";
  const stats = details?.stats ?? { totalOrders: 0, releasedOrders: 0, totalCryptoTraded: 0, totalFiatTraded: 0 };
  const orders = details?.orders ?? [];

  return (
    <main className="dashboard-main dashboard-subpage p2p-ad-details-page">
      <section className="p2p-ad-details-header">
        <h1 className="p2p-ad-details-title">P2P Ad Details</h1>
        <button type="button" className="p2p-ad-details-btn-back" onClick={() => navigate("/dashboard/p2p-ads")}>
          <i className="fas fa-arrow-left" /> Back to Ads List
        </button>
      </section>

      <div className="dashboard-body">
        {/* Ad Information */}
        <section className="p2p-ad-details-card">
          <h2 className="p2p-ad-details-card__title">Ad Information</h2>
          <div className="p2p-ad-details-params">
            <div className="p2p-ad-details-params__col">
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Ad UID</span><span className="p2p-ad-details-value">{ad.adUid}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Side</span><span className={`p2p-ad-details-badge p2p-ad-details-badge--side p2p-ad-details-badge--${(ad.side || "").toLowerCase()}`}>{ad.side}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Fiat Currency</span><span className="p2p-ad-details-value">{ad.fiatCurrency}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Quote Currency</span><span className="p2p-ad-details-value">{ad.qouteCurrency}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Fixed Price</span><span className="p2p-ad-details-value">{ad.fixedPrice}</span></div>
            </div>
            <div className="p2p-ad-details-params__col">
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Volume</span><span className="p2p-ad-details-value">{ad.volume}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Remaining Volume</span><span className="p2p-ad-details-value">{ad.remainingVolume}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Min Limit</span><span className="p2p-ad-details-value">{minLimit}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Max Limit</span><span className="p2p-ad-details-value">{maxLimit}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Status</span><span className={`p2p-ad-details-badge p2p-ad-details-badge--status p2p-ad-details-badge--${(ad.status || "").toLowerCase().replace("_", "-")}`}>{ad.status}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Online</span><span className={`p2p-ad-details-badge p2p-ad-details-badge--online p2p-ad-details-badge--${ad.isOnline ? "yes" : "no"}`}>{ad.isOnline ? "Yes" : "No"}</span></div>
            </div>
          </div>
          <div className="p2p-ad-details-owner">
            <h3 className="p2p-ad-details-owner__title">Ad Owner Details</h3>
            <div className="p2p-ad-details-owner__grid">
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Name</span><span className="p2p-ad-details-value">{ownerName}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Phone</span><span className="p2p-ad-details-value">{ownerPhone}</span></div>
              <div className="p2p-ad-details-row"><span className="p2p-ad-details-label">Email</span><span className="p2p-ad-details-value">{ownerEmail}</span></div>
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="p2p-ad-details-card">
          <h2 className="p2p-ad-details-card__title">Statistics</h2>
          <div className="p2p-ad-details-stats">
            <div className="p2p-ad-details-stat-box">
              <span className="p2p-ad-details-stat-value">{stats.totalOrders}</span>
              <span className="p2p-ad-details-stat-label">Total Orders</span>
            </div>
            <div className="p2p-ad-details-stat-box">
              <span className="p2p-ad-details-stat-value">{stats.releasedOrders}</span>
              <span className="p2p-ad-details-stat-label">Released Orders</span>
            </div>
            <div className="p2p-ad-details-stat-box">
              <span className="p2p-ad-details-stat-value">{stats.totalCryptoTraded}</span>
              <span className="p2p-ad-details-stat-label">Total Crypto Traded</span>
            </div>
            <div className="p2p-ad-details-stat-box">
              <span className="p2p-ad-details-stat-value">{stats.totalFiatTraded}</span>
              <span className="p2p-ad-details-stat-label">Total Fiat Traded</span>
            </div>
          </div>
        </section>

        {/* Orders */}
        <section className="p2p-ad-details-card">
          <h2 className="p2p-ad-details-card__title">Orders ({orders.length})</h2>
          <div className="p2p-ad-details-table-wrap">
            <table className="p2p-ad-details-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>ORDER ID</th>
                  <th>BUYER</th>
                  <th>SELLER</th>
                  <th>FIAT AMOUNT</th>
                  <th>CRYPTO AMOUNT</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={8} className="p2p-ad-details-empty">No orders</td></tr>
                ) : (
                  orders.map((row) => (
                    <tr key={row.sr}>
                      <td>{row.sr}</td>
                      <td>{row.orderId}</td>
                      <td>{row.buyer}</td>
                      <td>{row.seller}</td>
                      <td>{row.fiatAmount}</td>
                      <td>{row.cryptoAmount}</td>
                      <td><span className={`p2p-ad-details-order-status p2p-ad-details-order-status--${(row.status || "").toLowerCase().replace("_", "-")}`}>{row.status}</span></td>
                      <td>{formatDateTime(row.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default P2PAdDetailsPage;
